/**
 * NotificationDispatchHistoryPage — Tasks & Templates · Dispatch History.
 *
 * Searchable, read-only audit of every notification the Wave-8 dispatcher has
 * emitted (email / Teams DM / Teams card / SMS / in-app). One row per envelope
 * (recipients collapsed); the Recipients count is the fan-out width. Backed by
 * the dispatcher's NotificationDispatches table (design doc §6.2).
 *
 * Read: NotificationsApi.dispatches(filters) → GET /v3/notifications/dispatches
 *       envelope { success, count, rows: NotifDispatchRow[] }
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app surfaces no config
 * editors). No writes — pure audit. The backend filters server-side ONLY on
 * event code + date window + top (V3NotificationsFunction.Dispatches); the
 * row stats + Mode column roll up client-side. (template/recipient/mode are not
 * server-filterable on collapsed envelopes — removed as no-op controls per the
 * BACKLOG-0579 verification pass 2026-06-17.)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import type {
  Iso8601,
  NotifDispatchHistoryFilters,
  NotifDispatchRow,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField } from '@/components/FormField';

const DEFAULT_TOP = 100;

// Preset windows avoid a raw datetime-local primitive while still hitting the
// server's indexed `since` filter. 'any' omits the bound entirely.
const WINDOW_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

function windowToFromUtc(window: string): Iso8601 | undefined {
  if (window === 'any') return undefined;
  const ms =
    window === '24h' ? 86_400_000 : window === '7d' ? 604_800_000 : window === '30d' ? 2_592_000_000 : 0;
  if (ms === 0) return undefined;
  return new Date(Date.now() - ms).toISOString() as Iso8601;
}

function fmtTs(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtElapsed(req: string, completed?: string | null): string {
  if (!completed) return '—';
  const ms = new Date(completed).getTime() - new Date(req).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function priorityTone(p: string): 'err' | 'info' | 'muted' {
  return p === 'high' ? 'err' : p === 'low' ? 'muted' : 'info';
}

export function NotificationDispatchHistoryPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<NotifDispatchRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Filter inputs (staged; applied on Apply). Only event-code + window are
  // server-filterable; see the api-client dispatches() mapping.
  const [eventCode, setEventCode] = useState('');
  const [window, setWindow] = useState('any');
  const [showIds, setShowIds] = useState(false);

  // The filters actually in force (drive the fetch effect).
  const [applied, setApplied] = useState<NotifDispatchHistoryFilters>({ top: DEFAULT_TOP });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.notifications.dispatches(applied);
        if (cancelled) return;
        if (resp.success) {
          setRows(resp.rows ?? []);
        } else {
          setLoadError(resp.error ?? 'Failed to load dispatch history.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, applied, refreshNonce]);

  const applyFilters = useCallback(() => {
    const fromUtc = windowToFromUtc(window);
    setApplied({
      top: DEFAULT_TOP,
      ...(eventCode.trim() ? { eventCode: eventCode.trim() } : {}),
      ...(fromUtc ? { fromUtc } : {}),
    });
  }, [eventCode, window]);

  const clearFilters = useCallback(() => {
    setEventCode('');
    setWindow('any');
    setApplied({ top: DEFAULT_TOP });
  }, []);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const stats = useMemo(() => {
    let sent = 0;
    let skipped = 0;
    let failed = 0;
    for (const r of rows ?? []) {
      sent += r.sent;
      skipped += r.skipped;
      failed += r.failed;
    }
    return { sent, skipped, failed };
  }, [rows]);

  const exportCsv = useCallback(() => {
    if (!rows || rows.length === 0) return;
    const header = [
      'requestedAt',
      'completedAt',
      'eventCode',
      'templateCode',
      'recipientCount',
      'sent',
      'skipped',
      'failed',
      'isTestMode',
      'priority',
      'triggerActor',
    ];
    const lines = [header.join(',')];
    for (const r of rows) {
      const fields = [
        r.requestedAt,
        r.completedAt ?? '',
        r.eventCode,
        r.templateCode,
        String(r.recipientCount),
        String(r.sent),
        String(r.skipped),
        String(r.failed),
        String(r.isTestMode),
        r.priority,
        r.triggerActor ?? '',
      ];
      lines.push(
        fields.map((f) => (/[",\n]/.test(f) ? `"${f.replace(/"/g, '""')}"` : f)).join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notification-dispatches-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rows]);

  const columns: ReadonlyArray<DataTableColumn<NotifDispatchRow>> = useMemo(() => {
    const base: DataTableColumn<NotifDispatchRow>[] = [
      {
        header: 'Requested',
        cell: (r) => <span className="cell-desc">{fmtTs(r.requestedAt)}</span>,
      },
      {
        header: 'Event',
        cell: (r) => <code className="cell-mono">{r.eventCode}</code>,
      },
      {
        header: 'Template',
        cell: (r) => (
          <span>
            <code className="cell-mono">{r.templateCode}</code>
            {r.templateVersionId !== undefined && (
              <span className="cell-desc"> v{r.templateVersionId}</span>
            )}
          </span>
        ),
      },
      { header: 'Recipients', cell: (r) => <span className="num">{r.recipientCount}</span> },
      { header: 'Sent', cell: (r) => <span className="num">{r.sent}</span> },
      { header: 'Skipped', cell: (r) => <span className="num">{r.skipped}</span> },
      {
        header: 'Failed',
        cell: (r) =>
          r.failed > 0 ? <Pill tone="err">{r.failed}</Pill> : <span className="num">0</span>,
      },
      {
        header: 'Mode',
        cell: (r) => (r.isTestMode ? <Pill tone="warn">Test</Pill> : <Pill tone="ok">Live</Pill>),
      },
      { header: 'Priority', cell: (r) => <Pill tone={priorityTone(r.priority)}>{r.priority}</Pill> },
      {
        header: 'Elapsed',
        cell: (r) => <span className="cell-desc">{fmtElapsed(r.requestedAt, r.completedAt)}</span>,
      },
    ];
    if (showIds) {
      base.push({ header: 'Envelope ID', cell: (r) => <code className="cell-mono">{r.id}</code> });
    }
    return base;
  }, [showIds]);

  return (
    <>
      <Card
        title="Dispatch History"
        actions={
          <div className="card-actions-row">
            <Button onClick={refresh}>Refresh</Button>
            <Button variant="primary" onClick={exportCsv} disabled={!rows || rows.length === 0}>
              Export CSV
            </Button>
          </div>
        }
      >
        <div className="filter-grid">
          <TextField
            label="Event code"
            value={eventCode}
            onChange={setEventCode}
            placeholder="e.g. cart-processed"
            monospace
          />
          <SelectField label="Time window" value={window} onChange={setWindow} options={WINDOW_OPTIONS} />
        </div>
        <div className="card-actions-row filter-actions">
          <Button variant="primary" onClick={applyFilters}>
            Apply filters
          </Button>
          <Button onClick={clearFilters}>Clear</Button>
          <div className="filter-spacer" />
          <BoolField
            label="Show envelope IDs"
            value={showIds}
            onChange={setShowIds}
            hint="SuperAdmin debugging"
          />
        </div>
      </Card>

      {loadError ? (
        <Banner tone="err">
          Could not load dispatch history: {loadError} <Button onClick={refresh}>Retry</Button>
        </Banner>
      ) : !rows ? (
        <Spinner label="Loading dispatch history…" />
      ) : (
        <Card
          title={`${rows.length} envelopes`}
          actions={
            <span className="dispatch-stats">
              <strong className="stat-ok">{stats.sent}</strong> sent ·{' '}
              <strong>{stats.skipped}</strong> skipped ·{' '}
              <strong className="stat-err">{stats.failed}</strong> failed
            </span>
          }
        >
          <DataTable<NotifDispatchRow>
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            emptyState={<p className="empty">No dispatches match the current filters.</p>}
          />
        </Card>
      )}
    </>
  );
}
